import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal } from 'react-bootstrap';
import axios from 'axios';
import './ActivityManagement.css';

const ActivityManagement = () => {
  const [families, setFamilies] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Family Modal & State
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [familyModalMode, setFamilyModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [familyEmoji, setFamilyEmoji] = useState('💼');

  // Activity Modal & State
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityModalMode, setActivityModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [activityName, setActivityName] = useState('');
  const [activityEmoji, setActivityEmoji] = useState('💼');
  const [activityFamilyId, setActivityFamilyId] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [famRes, actRes] = await Promise.all([
        axios.get('/api/activities-hub/families'),
        axios.get('/api/activities-hub/activities')
      ]);
      setFamilies(famRes.data);
      setActivities(actRes.data);
    } catch (err) {
      console.error("Error fetching activities/families:", err);
      setError("Impossible de charger les données des activités.");
    } finally {
      setLoading(false);
    }
  };

  // --- Family Handlers ---
  const handleOpenFamilyModal = (mode, family = null) => {
    setFamilyModalMode(mode);
    if (mode === 'edit' && family) {
      setSelectedFamilyId(family._id);
      setFamilyName(family.name);
      setFamilyEmoji(family.emoji);
    } else {
      setSelectedFamilyId('');
      setFamilyName('');
      setFamilyEmoji('💼');
    }
    setShowFamilyModal(true);
  };

  const handleSaveFamily = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (familyModalMode === 'add') {
        await axios.post('/api/activities-hub/families', 
          { name: familyName, emoji: familyEmoji },
          { headers: { Authorization: token } }
        );
        setSuccess("Famille d'activité ajoutée avec succès !");
      } else {
        await axios.put(`/api/activities-hub/families/${selectedFamilyId}`, 
          { name: familyName, emoji: familyEmoji },
          { headers: { Authorization: token } }
        );
        setSuccess("Famille d'activité modifiée avec succès !");
      }
      setShowFamilyModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'enregistrement de la famille.");
    }
  };

  const handleDeleteFamily = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette famille d'activité ?")) return;
    setError('');
    setSuccess('');
    try {
      await axios.delete(`/api/activities-hub/families/${id}`, {
        headers: { Authorization: token }
      });
      setSuccess("Famille d'activité supprimée avec succès !");
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la suppression.");
    }
  };

  // --- Activity Handlers ---
  const handleOpenActivityModal = (mode, activity = null) => {
    setActivityModalMode(mode);
    if (mode === 'edit' && activity) {
      setSelectedActivityId(activity._id);
      setActivityName(activity.name);
      setActivityEmoji(activity.emoji);
      setActivityFamilyId(activity.family?._id || '');
    } else {
      setSelectedActivityId('');
      setActivityName('');
      setActivityEmoji('💼');
      setActivityFamilyId(families[0]?._id || '');
    }
    setShowActivityModal(true);
  };

  const handleSaveActivity = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (activityModalMode === 'add') {
        await axios.post('/api/activities-hub/activities', 
          { name: activityName, emoji: activityEmoji, familyId: activityFamilyId },
          { headers: { Authorization: token } }
        );
        setSuccess("Activité ajoutée avec succès !");
      } else {
        await axios.put(`/api/activities-hub/activities/${selectedActivityId}`, 
          { name: activityName, emoji: activityEmoji, familyId: activityFamilyId },
          { headers: { Authorization: token } }
        );
        setSuccess("Activité modifiée/réorganisée avec succès !");
      }
      setShowActivityModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'enregistrement de l'activité.");
    }
  };

  const handleDeleteActivity = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette activité ?")) return;
    setError('');
    setSuccess('');
    try {
      await axios.delete(`/api/activities-hub/activities/${id}`, {
        headers: { Authorization: token }
      });
      setSuccess("Activité supprimée avec succès !");
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la suppression.");
    }
  };

  return (
    <Container style={{ marginTop: '100px', marginBottom: '40px' }} className="activity-management-container">
      <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-4 rounded-4 shadow-sm border-bottom">
        <div>
          <h2 className="text-primary font-weight-bold mb-1">🛠️ Gestion des Activités</h2>
          <p className="text-muted mb-0">Ajoutez, modifiez et organisez les familles d'activités et les sous-activités de la plateforme.</p>
        </div>
        <Button variant="outline-primary" onClick={fetchData} disabled={loading}>
          {loading ? 'Chargement...' : 'Actualiser'}
        </Button>
      </div>

      {error && <div className="alert alert-danger mb-4 rounded-3">{error}</div>}
      {success && <div className="alert alert-success mb-4 rounded-3">{success}</div>}

      <Row className="g-4">
        {/* Families Card */}
        <Col lg={5}>
          <Card className="shadow-sm border-0 rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Card.Title as="h4" className="text-dark font-weight-bold mb-0">📦 Familles d'Activités</Card.Title>
                <Button variant="success" size="sm" onClick={() => handleOpenFamilyModal('add')} className="rounded-pill px-3">
                  + Ajouter
                </Button>
              </div>

              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead>
                    <tr>
                      <th>Emoji</th>
                      <th>Nom de la Famille</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {families.map(f => (
                      <tr key={f._id}>
                        <td className="fs-4">{f.emoji}</td>
                        <td className="fw-bold">{f.name}</td>
                        <td className="text-end">
                          <Button variant="outline-warning" size="sm" className="me-2 rounded-circle border-0" onClick={() => handleOpenFamilyModal('edit', f)}>
                            ✏️
                          </Button>
                          <Button variant="outline-danger" size="sm" className="rounded-circle border-0" onClick={() => handleDeleteFamily(f._id)}>
                            🗑️
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {families.length === 0 && (
                      <tr>
                        <td colSpan="3" className="text-center py-4 text-muted">Aucune famille définie.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Activities Card */}
        <Col lg={7}>
          <Card className="shadow-sm border-0 rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Card.Title as="h4" className="text-dark font-weight-bold mb-0">💇 Activités & Services</Card.Title>
                <Button variant="success" size="sm" onClick={() => handleOpenActivityModal('add')} className="rounded-pill px-3" disabled={families.length === 0}>
                  + Ajouter
                </Button>
              </div>

              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead>
                    <tr>
                      <th>Emoji</th>
                      <th>Activité</th>
                      <th>Famille Assignée</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map(a => (
                      <tr key={a._id}>
                        <td className="fs-4">{a.emoji}</td>
                        <td><strong>{a.name}</strong></td>
                        <td>
                          <Badge bg="info" className="px-3 py-2 text-uppercase" style={{ fontSize: '11px' }}>
                            {a.family?.emoji} {a.family?.name || 'Non Classé'}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <Button variant="outline-warning" size="sm" className="me-2 rounded-circle border-0" onClick={() => handleOpenActivityModal('edit', a)} title="Modifier / Réorganiser">
                            ✏️
                          </Button>
                          <Button variant="outline-danger" size="sm" className="rounded-circle border-0" onClick={() => handleDeleteActivity(a._id)}>
                            🗑️
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {activities.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center py-4 text-muted">Aucune activité définie.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Family Modal */}
      <Modal show={showFamilyModal} onHide={() => setShowFamilyModal(false)} centered>
        <Modal.Header closeButton style={{ background: 'var(--brand-navy)', color: '#fff' }}>
          <Modal.Title className="text-white">
            {familyModalMode === 'add' ? "➕ Ajouter une famille d'activités" : "✏️ Modifier la famille"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveFamily}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom de la Famille</Form.Label>
              <Form.Control
                type="text"
                value={familyName}
                onChange={e => setFamilyName(e.target.value)}
                required
                placeholder="Ex: Beauté, Sport, etc."
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Emoji représentatif</Form.Label>
              <Form.Control
                type="text"
                value={familyEmoji}
                onChange={e => setFamilyEmoji(e.target.value)}
                required
                placeholder="Ex: 💇, 🏋️"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowFamilyModal(false)}>Annuler</Button>
            <Button variant="success" type="submit">Enregistrer</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Activity Modal */}
      <Modal show={showActivityModal} onHide={() => setShowActivityModal(false)} centered>
        <Modal.Header closeButton style={{ background: 'var(--brand-navy)', color: '#fff' }}>
          <Modal.Title className="text-white">
            {activityModalMode === 'add' ? "➕ Ajouter une activité" : "✏️ Modifier / Réorganiser l'activité"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveActivity}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom de l'Activité</Form.Label>
              <Form.Control
                type="text"
                value={activityName}
                onChange={e => setActivityName(e.target.value)}
                required
                placeholder="Ex: Coiffure, Massage, Yoga"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Emoji représentatif</Form.Label>
              <Form.Control
                type="text"
                value={activityEmoji}
                onChange={e => setActivityEmoji(e.target.value)}
                required
                placeholder="Ex: 💇, 💅"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Famille d'activités (Organisation)</Form.Label>
              <Form.Select
                value={activityFamilyId}
                onChange={e => setActivityFamilyId(e.target.value)}
                required
              >
                {families.map(f => (
                  <option key={f._id} value={f._id}>{f.emoji} {f.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowActivityModal(false)}>Annuler</Button>
            <Button variant="success" type="submit">Enregistrer</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default ActivityManagement;
